import { Component, OnInit, OnDestroy, inject, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { OrganizationActivityService } from '../../core/data-services/organization-activity.service';
import { OrganizationActivity } from '../../core/data-models/organization-activity.model';
import { getActivityIcon, getActivityRelativeTime, getResourceRoute } from '../../core/utils/organization-activity-utils';

/**
 * Activity Page Component
 * Full page view of all organization activities with pagination
 *
 * Reference: /agent-os/specs/2026-01-16-dashboard-updates/spec.md
 */
@Component({
  selector: 'app-activity',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './activity.component.html',
  styleUrls: ['./activity.component.scss']
})
export class ActivityComponent implements OnInit, OnDestroy {
  private activityService = inject(OrganizationActivityService);
  private router = inject(Router);

  // Loading state for initial load
  isInitializing = signal(true);

  // Filter state: show only unseen activities
  showUnseenOnly = signal(false);

  // Activities from OrganizationActivityService (filtered based on showUnseenOnly)
  readonly allActivities = computed(() => this.activityService.activities$());
  readonly unseenActivities = computed(() => this.activityService.unseenActivities$());
  readonly activities = computed(() =>
    this.showUnseenOnly() ? this.unseenActivities() : this.allActivities()
  );
  readonly isLoadingActivities = computed(() => this.activityService.isLoadingActivities$());
  readonly hasMore = computed(() => this.activityService.hasMore$());
  readonly newActivityIds = computed(() => this.activityService.newActivityIds$());
  readonly unseenCount = computed(() => this.activityService.unseenActivityCount$());

  async ngOnInit(): Promise<void> {
    // Initialize dashboard data (which includes activities)
    await this.activityService.initialize();
    this.isInitializing.set(false);

    // Mark activities as seen when viewing the activity page
    await this.activityService.markActivitiesAsSeen();
  }

  ngOnDestroy(): void {
    // OrganizationActivityService handles its own cleanup
  }

  /**
   * Toggle filter between all activities and unseen only
   */
  toggleUnseenFilter(): void {
    this.showUnseenOnly.update(v => !v);
  }

  /**
   * Get the icon class for an activity based on its event type
   */
  getActivityIconClass(activity: OrganizationActivity): string {
    return getActivityIcon(activity.eventType);
  }

  /**
   * Get relative time string for an activity
   */
  getRelativeTime(createdAt: Date | null): string {
    return getActivityRelativeTime(createdAt);
  }


  /**
   * Check if activity has a navigable resource
   */
  hasResourceLink(activity: OrganizationActivity): boolean {
    return !!activity.resourceType && !!activity.resourceId;
  }

  /**
   * Navigate to activity resource
   */
  navigateToResource(activity: OrganizationActivity): void {
    const route = getResourceRoute(activity.resourceType, activity.resourceId);
    if (route) {
      this.router.navigate([route]);
    }
  }

  /**
   * Check if activity is newly added (for animation)
   */
  isNewActivity(activityId: string): boolean {
    return this.newActivityIds().has(activityId);
  }

  /**
   * Load more activities (pagination)
   */
  async loadMore(): Promise<void> {
    await this.activityService.loadMoreActivities();
  }

  /**
   * Refresh activities list
   */
  async refresh(): Promise<void> {
    await this.activityService.refreshActivities();
  }
}
